/**
 * « Ajouter des cibles à suivre » (onboarding page 1, inside « Options avancées »):
 * pick a type, then a method (à la main · sitemap · CSV), and the entry joins the in-memory
 * queue written right after the brand is created (pendingTargets.ts).
 */
import { useRef, useState } from 'react';
import { Box, Button, IconButton, TextField, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditNoteIcon from '@mui/icons-material/EditNote';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useTranslation } from 'react-i18next';
import { CAT_BY_ID } from '../targets/CATEGORIES';
import { downloadCsv } from '../targets/csv';
import { formatNumber } from '../../i18n/format';
import {
  METHODS_BY_CATEGORY,
  canQueueBulk,
  type ManualRow,
  type PendingTarget,
  type QueueCategory,
  type QueueMethod,
} from './pendingTargets';

const CATEGORY_ORDER: QueueCategory[] = ['produit', 'collection', 'contenu', 'etablissement', 'personne', 'marque'];

const METHOD_ICON: Record<QueueMethod, React.ReactNode> = {
  manual: <EditNoteIcon sx={{ fontSize: 18 }} />,
  sitemap: <TravelExploreIcon sx={{ fontSize: 18 }} />,
  csv: <UploadFileIcon sx={{ fontSize: 18 }} />,
};

const EMPTY_ROWS: ManualRow[] = [{ name: '', url: '' }];

function categoryLabel(cat: QueueCategory, t: (k: string) => string): string {
  // « Marques » in Cibles; here the brand itself already exists, so these are sub-brands.
  return cat === 'marque' ? t('onboarding.setup.targets.subBrands') : t(CAT_BY_ID[cat].labelKey);
}

export function TargetQueueEditor({
  domain,
  queue,
  onChange,
}: {
  domain: string;
  queue: PendingTarget[];
  onChange: (queue: PendingTarget[]) => void;
}) {
  const { t, i18n } = useTranslation();
  const [category, setCategory] = useState<QueueCategory | null>(null);
  const [method, setMethod] = useState<QueueMethod | null>(null);
  const [rows, setRows] = useState<ManualRow[]>(EMPTY_ROWS);
  const [file, setFile] = useState<File | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const bulkAllowed = canQueueBulk(queue);

  const reset = () => { setMethod(null); setRows(EMPTY_ROWS); setFile(null); };
  const add = (entry: PendingTarget) => { onChange([...queue, entry]); reset(); };

  const manualReady = rows.some((r) => (category === 'marque' ? r.name.trim() : r.url.trim()));

  return (
    <Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' }, gap: 1 }}>
        {CATEGORY_ORDER.map((cat) => {
          const cfg = CAT_BY_ID[cat];
          const on = category === cat;
          return (
            <Box
              key={cat}
              component="button"
              type="button"
              onClick={() => { setCategory(cat); reset(); }}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.25, p: 1.5, borderRadius: 3, cursor: 'pointer',
                border: '1px solid', borderColor: on ? 'primary.main' : 'divider', bgcolor: on ? 'primary.50' : 'background.paper',
                color: on ? 'primary.main' : 'text.primary', fontWeight: 600, fontSize: 13, fontFamily: 'inherit', textAlign: 'left',
                '&:hover': { borderColor: 'primary.light' },
              }}
            >
              <cfg.Icon sx={{ fontSize: 19, color: on ? 'primary.main' : cfg.color }} />
              {categoryLabel(cat, t)}
            </Box>
          );
        })}
      </Box>

      {category && (
        <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
          {(['manual', 'sitemap', 'csv'] as QueueMethod[]).map((m) => {
            const offered = METHODS_BY_CATEGORY[category].includes(m);
            const blocked = m !== 'manual' && !bulkAllowed;
            const reason = !offered
              ? m === 'manual'
                ? t('onboarding.setup.targets.manualInCibles')
                : t('onboarding.setup.targets.sitemapCatalogOnly')
              : blocked
                ? t('onboarding.setup.targets.oneImport')
                : '';
            const btn = (
              <Button
                variant={method === m ? 'contained' : 'outlined'}
                color={method === m ? 'primary' : 'inherit'}
                size="small"
                startIcon={METHOD_ICON[m]}
                disabled={!offered || blocked}
                onClick={() => { setMethod(m); setRows(EMPTY_ROWS); setFile(null); }}
                sx={{ borderRadius: 2.5, fontWeight: 600, borderColor: 'divider' }}
              >
                {t(`onboarding.setup.targets.method.${m}`)}
              </Button>
            );
            return reason ? <Tooltip key={m} title={reason} arrow><span>{btn}</span></Tooltip> : <Box key={m}>{btn}</Box>;
          })}
        </Box>
      )}

      {category && method === 'manual' && (
        <Panel>
          {rows.map((r, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 1, mb: 0.75 }}>
              {category !== 'contenu' && (
                <TextField
                  size="small"
                  value={r.name}
                  onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
                  placeholder={t('onboarding.setup.targets.namePlaceholder')}
                  sx={{ flex: 1 }}
                />
              )}
              <TextField
                size="small"
                value={r.url}
                onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, url: e.target.value } : x)))}
                placeholder={category === 'marque' ? t('onboarding.setup.targets.domainPlaceholder') : t('onboarding.setup.targets.urlPlaceholder')}
                sx={{ flex: 1.4 }}
              />
            </Box>
          ))}
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Button size="small" onClick={() => setRows([...rows, { name: '', url: '' }])}>{t('onboarding.setup.targets.addRow')}</Button>
            <Box sx={{ flex: 1 }} />
            <Button
              size="small"
              variant="contained"
              disabled={!manualReady}
              onClick={() => add({ kind: 'manual', category: category as 'produit' | 'collection' | 'contenu' | 'marque', rows: rows.filter((r) => r.name.trim() || r.url.trim()) })}
            >
              {t('onboarding.setup.targets.add')}
            </Button>
          </Box>
        </Panel>
      )}

      {category && method === 'sitemap' && (
        <Panel>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <TravelExploreIcon sx={{ color: 'text.secondary' }} />
            <Box sx={{ flex: 1 }}>
              <Box sx={{ fontWeight: 600, fontSize: 13.5 }}>{t('onboarding.setup.targets.sitemapOf', { domain })}</Box>
              <Box sx={{ fontSize: 12, color: 'text.disabled' }}>{t('onboarding.setup.targets.sitemapUpTo')}</Box>
            </Box>
            <Button size="small" variant="contained" disabled={!domain} onClick={() => add({ kind: 'sitemap', domain })}>
              {t('onboarding.setup.targets.add')}
            </Button>
          </Box>
        </Panel>
      )}

      {category && method === 'csv' && (
        <Panel>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            hidden
            onChange={(e) => { setFile(e.target.files?.[0] ?? null); e.target.value = ''; }}
          />
          {file ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <UploadFileIcon sx={{ color: 'text.secondary' }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ fontWeight: 600, fontSize: 13.5, overflow: 'hidden', textOverflow: 'ellipsis' }}>{file.name}</Box>
                <Box sx={{ fontSize: 12, color: 'text.disabled' }}>{formatBytes(file.size)}</Box>
              </Box>
              <Button size="small" onClick={() => setFile(null)}>{t('onboarding.common.remove')}</Button>
              <Button size="small" variant="contained" onClick={() => add({ kind: 'csv', category, file })}>
                {t('onboarding.setup.targets.add')}
              </Button>
            </Box>
          ) : (
            <Box
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) setFile(f); }}
              sx={{ border: '1.5px dashed', borderColor: 'divider', borderRadius: 2.5, p: 2.5, textAlign: 'center', fontSize: 12.5, color: 'text.secondary', bgcolor: 'background.paper' }}
            >
              <UploadFileIcon sx={{ fontSize: 22, color: 'text.disabled' }} />
              <Box>
                {t('onboarding.setup.targets.csvDrop')}{' '}
                <Box component="button" type="button" onClick={() => fileRef.current?.click()} sx={linkSx}>
                  {t('onboarding.setup.targets.csvBrowse')}
                </Box>
              </Box>
              <Box
                component="button"
                type="button"
                onClick={() => {
                  const cfg = CAT_BY_ID[category];
                  const lang = i18n.language?.startsWith('en') ? 'en' : 'fr';
                  downloadCsv(`template-${category}.csv`, cfg.csvTemplates[lang] ?? cfg.csvTemplates.fr);
                }}
                sx={{ ...linkSx, fontSize: 12, mt: 0.75 }}
              >
                {t('onboarding.setup.targets.csvTemplate')}
              </Box>
            </Box>
          )}
        </Panel>
      )}

      {queue.map((q, i) => {
        const cat: QueueCategory = q.kind === 'sitemap' ? 'produit' : q.category;
        const cfg = CAT_BY_ID[cat];
        const what =
          q.kind === 'manual'
            ? t('onboarding.setup.targets.manualCount', { count: q.rows.length })
            : q.kind === 'sitemap'
              ? t('onboarding.setup.targets.sitemapOf', { domain: q.domain })
              : q.file.name;
        return (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mt: 1, px: 1.5, py: 1, borderRadius: 2.5, border: '1px solid', borderColor: 'rgba(124,58,237,.18)', fontSize: 13 }}>
            <cfg.Icon sx={{ fontSize: 17, color: cfg.color }} />
            <Box component="b">{q.kind === 'sitemap' ? t('onboarding.setup.targets.catalog') : categoryLabel(cat, t)}</Box>
            <Box sx={{ color: 'text.secondary', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{what}</Box>
            <IconButton size="small" aria-label={t('onboarding.common.remove')} onClick={() => onChange(queue.filter((_, j) => j !== i))}>
              <CloseIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Box>
        );
      })}
    </Box>
  );
}

const linkSx = {
  border: 'none', background: 'none', p: 0, color: 'primary.main', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', fontSize: 'inherit',
} as const;

function Panel({ children }: { children: React.ReactNode }) {
  return <Box sx={{ border: '1px solid', borderColor: 'divider', bgcolor: '#fbfbfd', borderRadius: 3, p: 1.75, mt: 1.5 }}>{children}</Box>;
}

function formatBytes(n: number): string {
  const [value, unit] = n < 1024 * 1024 ? [n / 1024, 'kilobyte'] : [n / (1024 * 1024), 'megabyte'];
  return formatNumber(value, { style: 'unit', unit, maximumFractionDigits: 1 });
}
